import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../utils/firebase';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import './Login.css';

export default function Login() {
  const { login: ctxLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [adminMode, setAdminMode] = useState(false);
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const autoRequested = useRef(false);

  // Si llegamos desde aldealix.org con ?email=..., pedimos el código
  // automáticamente y mostramos directamente el paso del token.
  useEffect(() => {
    const emailParam = new URLSearchParams(window.location.search).get('email');
    if (!emailParam || autoRequested.current) return;
    autoRequested.current = true;
    const trimmed = emailParam.trim();
    setEmail(trimmed);
    setLoading(true);
    api
      .requestMagicLink(trimmed)
      .then(() => setSent(true))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const trimmedEmail = email.trim();
    setEmail(trimmedEmail);
    try {
      await api.requestMagicLink(trimmedEmail);
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await api.verifyMagicLink(null, code, email);
      ctxLogin(data.token, data.user);
      navigate('/eventos');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setCode('');
    setLoading(true);
    try {
      await api.requestMagicLink(email);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-bg-runes">ᚠᚢᚦᚨᚱᚲᚷᚹᚺᚾᛁᛃᛇᛈᛉᛊᛏᛒᛖᛗᛚᛜᛞᛟ</div>
      <div className="login-card">
        <div className="login-brand">
          <div className="login-rune">ᚠ</div>
          <h1 className="login-title">Aldealix</h1>
          <p className="login-sub">La Aldea Celta · Reservas</p>
        </div>
        <div className="login-divider" />

        {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}

        {/* Paso 1: introducir email */}
        {!sent && !adminMode && (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Correo electrónico</label>
              <input
                className="form-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="tu@email.com"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading}>
              {loading ? <><span className="spinner" /> Enviando email...</> : 'Acceder a la Aldea'}
            </button>
            <p style={{ textAlign: 'center', marginTop: '1.2rem', fontSize: '.82rem', color: 'var(--mid)' }}>
              <span
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => { setAdminMode(true); setError(''); }}
              >
                Acceso administrador
              </span>
            </p>
          </form>
        )}

        {/* Paso 2: introducir código (misma pantalla) */}
        {sent && !adminMode && (
          <form onSubmit={handleVerify}>
            <div style={{ marginBottom: '1.2rem' }}>
              <div style={{ fontSize: '.75rem', fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--sage)', marginBottom: '.3rem' }}>
                Email enviado a
              </div>
              <div style={{ fontSize: '.9rem', color: 'var(--white)' }}>{email}</div>
            </div>

            <div className="form-group">
              <label className="form-label">Código de acceso</label>
              <input
                className="form-input"
                type="text"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                autoFocus
                required
                style={{ fontSize: '1.5rem', letterSpacing: '.5rem', textAlign: 'center' }}
              />
              <div className="form-hint">Introduce el código de 6 dígitos que te hemos enviado por email</div>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-full btn-lg"
              disabled={loading || code.length !== 6}
            >
              {loading ? <><span className="spinner" /> Verificando...</> : 'Entrar a la Aldea'}
            </button>

            <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '.78rem', color: 'var(--mid)' }}>
              ¿No te llegó?{' '}
              <span
                style={{ cursor: 'pointer', textDecoration: 'underline', color: 'var(--sage)' }}
                onClick={handleResend}
              >
                Reenviar código
              </span>
              {' · '}
              <span
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => { setSent(false); setCode(''); setError(''); }}
              >
                Cambiar email
              </span>
            </p>
          </form>
        )}

        {/* Admin: Google Sign-In */}
        {adminMode && (
          <div>
            <p style={{ fontSize: '.88rem', color: 'var(--mid)', marginBottom: '1.2rem', textAlign: 'center' }}>
              Accede con tu cuenta de Google autorizada como administrador o moderador.
            </p>
            <button
              type="button"
              className="btn btn-full btn-lg"
              disabled={loading}
              onClick={async () => {
                setError('');
                setLoading(true);
                try {
                  const provider = new GoogleAuthProvider();
                  const result = await signInWithPopup(auth, provider);
                  const idToken = await result.user.getIdToken();
                  const data = await api.loginWithGoogle(idToken);
                  ctxLogin(data.token, data.user);
                  const role = data.user?.role;
                  navigate(role === 'moderator' ? '/moderador' : '/admin');
                } catch (err) {
                  setError(err.message || 'Error al iniciar sesión con Google');
                } finally {
                  setLoading(false);
                }
              }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.6rem',
                background: 'var(--surface2)', border: '1px solid var(--green-border)',
                color: 'var(--white)', fontWeight: 600,
              }}
            >
              {loading ? (
                <><span className="spinner" /> Conectando...</>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Acceder con Google
                </>
              )}
            </button>
            <p style={{ textAlign: 'center', marginTop: '1.2rem', fontSize: '.82rem', color: 'var(--mid)' }}>
              <span
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => { setAdminMode(false); setError(''); }}
              >
                ← Volver al acceso normal
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
